/**
 * usePortfolioValues Hook
 *
 * Computes portfolio balances, total value, yield earned, and loading state.
 * Token-amount fields are returned as BigDecimal so consumers never carry
 * precision loss into downstream arithmetic or transaction submission. The
 * lossy `.toNumber()` belongs at the consumer edge (e.g. `useCountUp`,
 * `DeltaBadge`, `useBalanceSeek`) — never here.
 * Shared between PortfolioSection (Home) and PortfolioOverview (Portfolio page).
 */

import { useSimMode } from "@shared/sim";
import { useTokenBalancesData, useWallet } from "@solomon-apps/shared";
import { TOKEN_DECIMALS } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { useMemo } from "react";

import { useStakingStats, useUserStakingStats } from "@/hooks/staking";
import { useReportingLiveApy } from "@/hooks/stats/useReportingLiveApy";

interface PortfolioValues {
    usdvBalance: BigDecimal | null;
    susdvBalance: BigDecimal | null;
    sharePrice: number;
    stakedUsdvValue: BigDecimal | null;
    totalValue: BigDecimal | null;
    totalYield: BigDecimal;
    monthlyYield: BigDecimal | null;
    monthToDateApy: number | null;
    formattedApy: string;
    isLoaded: boolean;
}

function useSimPortfolioValues(): PortfolioValues {
    const usdvBalance = new BigDecimal("1234.56");
    const susdvBalance = new BigDecimal("500.00");
    const sharePrice = 1.05;
    const sharePriceBd = new BigDecimal(sharePrice);
    const stakedUsdvValue = susdvBalance.multiply(sharePriceBd);
    return {
        usdvBalance,
        susdvBalance,
        sharePrice,
        stakedUsdvValue,
        totalValue: usdvBalance.add(stakedUsdvValue),
        totalYield: new BigDecimal("87.65"),
        monthlyYield: new BigDecimal("7.30"),
        monthToDateApy: 7.1,
        formattedApy: "7.1%",
        isLoaded: true
    };
}

export function usePortfolioValues(): PortfolioValues {
    const sim = useSimMode();
    const useImplementation = sim.enabled ? useSimPortfolioValues : useLivePortfolioValues;
    return useImplementation();
}

function useLivePortfolioValues(): PortfolioValues {
    const { publicKey } = useWallet();
    const { balances, isLoading: isBalancesLoading } = useTokenBalancesData();
    const { data: stakingStats, isLoading: isStatsLoading } = useStakingStats();
    const { data: userStats } = useUserStakingStats(publicKey?.toString() ?? null);
    const { formattedApy } = useReportingLiveApy();

    const sharePriceBd = useMemo(() => {
        if (!stakingStats?.sharePrice) return new BigDecimal(1);
        return new BigDecimal(stakingStats.sharePrice);
    }, [stakingStats?.sharePrice]);
    const sharePrice = sharePriceBd.toNumber();

    const usdvBalance = balances.usdv;
    const susdvBalance = balances.susdv;

    const stakedUsdvValue = useMemo<BigDecimal | null>(() => {
        if (!susdvBalance) return null;
        return susdvBalance.multiply(sharePriceBd);
    }, [susdvBalance, sharePriceBd]);

    const totalValue = useMemo<BigDecimal | null>(() => {
        if (
            (usdvBalance === null || usdvBalance === undefined) &&
            (stakedUsdvValue === null || stakedUsdvValue === undefined)
        )
            return null;
        return (usdvBalance ?? new BigDecimal(0)).add(stakedUsdvValue ?? new BigDecimal(0));
    }, [usdvBalance, stakedUsdvValue]);

    const totalYield = useMemo<BigDecimal>(() => {
        if (userStats?.yieldStats?.totalYield === null || userStats?.yieldStats?.totalYield === undefined)
            return new BigDecimal(0);
        return new BigDecimal(userStats.yieldStats.totalYield).divide(BigDecimal.pow10(TOKEN_DECIMALS.USDv));
    }, [userStats]);

    const monthlyYield = useMemo<BigDecimal | null>(() => {
        // Treat `0` (and `"0"`, `"0.0"`, etc.) as a real value — only
        // null/undefined means "not loaded yet". Falsy check would coerce
        // a real-zero MTD amount into a missing-data signal, hiding the
        // row in PortfolioCard. Mirrors the sibling monthToDateApy check.
        if (
            userStats?.performance?.monthToDateAmount === null ||
            userStats?.performance?.monthToDateAmount === undefined
        )
            return null;
        return new BigDecimal(userStats.performance.monthToDateAmount).divide(BigDecimal.pow10(TOKEN_DECIMALS.USDv));
    }, [userStats]);

    const monthToDateApy = useMemo(() => {
        if (userStats?.performance?.monthToDateApy === null || userStats?.performance?.monthToDateApy === undefined)
            return null;
        return Number(userStats.performance.monthToDateApy) * 100;
    }, [userStats]);

    const hasBalanceData = balances.usdv !== null || balances.susdv !== null;
    const isLoaded = hasBalanceData || (!isBalancesLoading && !isStatsLoading);

    return {
        usdvBalance,
        susdvBalance,
        sharePrice,
        stakedUsdvValue,
        totalValue,
        totalYield,
        monthlyYield,
        monthToDateApy,
        formattedApy,
        isLoaded
    };
}
